import type {
  StaffMemoryEvidence,
  StaffMemoryFact,
  StaffMemoryFactDetail,
} from "@lafayette/schemas";
import { createColumnHelper } from "@tanstack/react-table";
import { type ReactNode, useId } from "react";
import { Link, useParams } from "react-router";

import { citedMessageHref } from "../../conversations/cited-message";
import { recordLinkFor } from "../../diagnostics/record-links";
import { useOffering } from "../../navigation/use-offering";
import { AsyncState } from "../../ui/async-state";
import { cn } from "../../ui/cn";
import { DataTable } from "../../ui/data-table";
import { BackLink } from "../../ui/links";
import { PageHeader } from "../../ui/page-header";
import { useMemoryCapabilities } from "../access";
import {
  detailPath,
  FactBadges,
  FactMeta,
  FactStatusBadge,
  FactValue,
  MetaItem,
  MetaList,
  nameWith,
  ProvenanceBadge,
} from "../components/parts";
import { describeMemoryError } from "../errors";
import { formatDateTime } from "../format";
import {
  actorKindLabels,
  auditActionLabel,
  confirmationLabels,
  evidenceEventLabels,
  evidenceMessageAuthorLabels,
  factValueLabel,
  formLabels,
  kindLabel,
  reasonCodeLabels,
  relationshipLabels,
  runStatusLabels,
  runTriggerLabels,
  sensitivityLabels,
  sourceReferenceDescriptions,
  sourceTypeLabels,
} from "../labels";
import { useMemoryFact } from "../queries";
import { useMemoryRuntime } from "../runtime";
import { EmptyNote, MemorySection } from "../sections/FactSections";

/**
 * M-S3. One fact: where it came from, the evidence (who sent each message and when, with a link to the
 * conversation), the extraction run, the supersession chain and the audit trail. The page is read only; the
 * actions live on the workspace. It has no model reasoning and no message text to show: Postgres stores
 * none, and the memory API does not read Stream (CHT-1984, ADR 0012).
 */
export function MemoryFactPage() {
  const { customerId = "", factId = "" } = useParams();
  const { now } = useMemoryRuntime();
  const { capabilities, isLoading: rolesLoading } = useMemoryCapabilities();
  const detail = useMemoryFact(factId, capabilities.read);
  const back = `/customers/${encodeURIComponent(customerId)}/memory`;

  if (rolesLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.read) {
    return (
      <AsyncState
        kind="error"
        title="Not available for your role"
        message="Your staff role does not include customer memory."
      />
    );
  }
  if (detail.isPending) {
    return (
      <AsyncState kind="loading" title="Loading the memory" message="Waiting for the answer." />
    );
  }
  if (detail.isError) {
    const failure = describeMemoryError(detail.error);
    return failure.kind === "forbidden" ? (
      <AsyncState kind="error" title="You cannot open this" message={failure.message} />
    ) : (
      <AsyncState
        kind="error"
        title="The memory did not load"
        message={failure.message}
        onRetry={() => void detail.refetch()}
      />
    );
  }

  const { fact } = detail.data;
  return (
    <section className="grid grid-cols-[minmax(0,1fr)] gap-6" aria-labelledby="page-title">
      <PageHeader
        breadcrumbs={<BackLink to={back}>Back to the customer's memory</BackLink>}
        eyebrow="Memory detail"
        title={factValueLabel(fact)}
        description={`${kindLabel(fact.kind)}. Your relationship: ${relationshipLabels[detail.data.relationship]}. This page only reads. Change a memory from the customer's memory page.`}
      />

      <MemorySection id="fact-summary" title="Summary" className="gap-4">
        <FactValue fact={fact} />
        <FactBadges fact={fact} />
        <FactMeta fact={fact} now={now()} />
        <div className="border-t border-hairline pt-4">
          <StaffFields fact={fact} customerId={customerId} />
        </div>
      </MemorySection>

      <RelatedFacts detail={detail.data} customerId={customerId} />
      <EvidenceSection detail={detail.data} />
      <RunSection detail={detail.data} />
      <ChainSection detail={detail.data} customerId={customerId} />
      <AuditSection detail={detail.data} />
    </section>
  );
}

function StaffFields({
  fact,
  customerId,
}: Readonly<{ fact: StaffMemoryFact; customerId: string }>) {
  return (
    <MetaList wide>
      <MetaItem term="Form">{fact.form === null ? "Value erased" : formLabels[fact.form]}</MetaItem>
      <MetaItem term="Confirmation">{confirmationLabels[fact.confirmationLevel]}</MetaItem>
      <MetaItem term="Source">
        {sourceTypeLabels[fact.sourceType]}
        {fact.sourceReference === null
          ? null
          : ` (${sourceReferenceDescriptions[fact.sourceReference.kind]})`}
      </MetaItem>
      <MetaItem term="Sensitivity">{sensitivityLabels[fact.sensitivity]}</MetaItem>
      <MetaItem term="Policy version">
        {fact.policyVersion ?? "Older record, no policy version"}
      </MetaItem>
      <MetaItem term="Written by">{actorKindLabels[fact.createdByKind]}</MetaItem>
      <MetaItem term="Last changed by">{actorKindLabels[fact.updatedByKind]}</MetaItem>
      <MetaItem term="First observed">{formatDateTime(fact.firstObservedAt)}</MetaItem>
      <MetaItem term="Status changed">{formatDateTime(fact.statusChangedAt)}</MetaItem>
      <MetaItem term="Created">{formatDateTime(fact.createdAt)}</MetaItem>
      <MetaItem term="Evidence rows">{fact.evidenceCount}</MetaItem>
      {fact.supersedesFactId === null ? null : (
        <MetaItem term="Replaced an earlier memory">
          <Link
            className="text-link underline underline-offset-4"
            to={detailPath(customerId, fact.supersedesFactId)}
          >
            Open the earlier memory
          </Link>
        </MetaItem>
      )}
      {fact.successorFactId === null ? null : (
        <MetaItem term="Replaced by">
          <Link
            className="text-link underline underline-offset-4"
            to={detailPath(customerId, fact.successorFactId)}
          >
            Open the newer memory
          </Link>
        </MetaItem>
      )}
    </MetaList>
  );
}

const lineClasses = "m-0 flex min-w-0 flex-wrap items-center gap-2 [overflow-wrap:anywhere]";
const linkClasses =
  "inline-flex min-h-11 items-center text-link underline underline-offset-4 hover:no-underline compact:min-h-8";

function FactLine({ fact, customerId }: Readonly<{ fact: StaffMemoryFact; customerId: string }>) {
  return (
    <li className={lineClasses}>
      <Link className={linkClasses} to={detailPath(customerId, fact.id)}>
        {factValueLabel(fact)}
      </Link>{" "}
      <FactStatusBadge status={fact.status} /> <ProvenanceBadge provenance={fact.provenance} />
    </li>
  );
}

const Lines = ({ children }: Readonly<{ children: ReactNode }>) => (
  <ul className="m-0 grid list-none gap-2 p-0">{children}</ul>
);

const Hint = ({ children }: Readonly<{ children: ReactNode }>) => (
  <p className="m-0 text-sm text-muted-foreground">{children}</p>
);

function RelatedFacts({
  detail,
  customerId,
}: Readonly<{ detail: StaffMemoryFactDetail; customerId: string }>) {
  if (detail.challenges === null && detail.challengedBy.length === 0) return null;
  return (
    <MemorySection id="fact-related" title="Challenges">
      {detail.challenges === null ? null : (
        <>
          <h3 className="m-0 text-base font-semibold">This candidate challenges</h3>
          <Lines>
            <FactLine fact={detail.challenges} customerId={customerId} />
          </Lines>
        </>
      )}
      {detail.challengedBy.length === 0 ? null : (
        <>
          <h3 className="m-0 text-base font-semibold">Candidates that challenge this memory</h3>
          <Lines>
            {detail.challengedBy.map((fact) => (
              <FactLine key={fact.id} fact={fact} customerId={customerId} />
            ))}
          </Lines>
        </>
      )}
    </MemorySection>
  );
}

/**
 * What one evidence row says, in words (CHT-1984): "A message from the customer, sent 12 Oct 2026, 09:30". The raw id of the message is
 * never shown. A row without a chat message (another kind of source, or a message that Postgres has no row for) says what kind of source it is.
 */
function evidenceSummary(row: StaffMemoryEvidence): string {
  const message = row.message;
  if (row.sourceReferenceKind !== "CONVERSATION_MESSAGE") {
    return sourceReferenceDescriptions[row.sourceReferenceKind];
  }
  if (message === null)
    return `${sourceReferenceDescriptions.CONVERSATION_MESSAGE}, sent ${formatDateTime(row.observedAt)}`;
  const edited = message.state === "EDITED" ? ", edited later" : "";
  const removed = message.state === "DELETED" ? ", since removed" : "";
  return `A message from ${evidenceMessageAuthorLabels[message.authorKind]}, sent ${formatDateTime(message.sentAt)}${edited}${removed}`;
}

function EvidenceSection({ detail }: Readonly<{ detail: StaffMemoryFactDetail }>) {
  const offering = useOffering();
  return (
    <MemorySection id="fact-evidence" title={`Evidence (${detail.evidence.length})`}>
      <Hint>
        Evidence shows who sent the message and when. The text of the message stays in the
        conversation. Lafayette does not keep it here, and it does not keep the reasoning of the
        assistant.
      </Hint>
      {detail.evidence.length === 0 ? (
        <EmptyNote>
          {detail.fact.createdByKind === "system"
            ? "No evidence rows are recorded for this memory."
            : "No evidence rows. A person wrote this memory."}
        </EmptyNote>
      ) : (
        <ul className="m-0 grid list-none p-0">
          {detail.evidence.map((row, index) => {
            // The link grants nothing: the conversation page checks the staff member's access again. It is offered only to a role that has
            // the Concierge area, so it never ends in a refusal.
            const conversationHref =
              row.message === null
                ? null
                : recordLinkFor("conversation", row.message.conversationId, offering).href;
            // The link also names the message, so the conversation opens at it (CHT-1984). The id is a plain token: it is checked in the helper.
            const conversation =
              conversationHref === null
                ? null
                : citedMessageHref(conversationHref, row.sourceReferenceId);
            return (
              <li
                // Two evidence rows can look the same, so the position is part of the key.
                // biome-ignore lint/suspicious/noArrayIndexKey: rows are never reordered
                key={`${index}-${row.event}-${row.sourceReferenceKind}-${row.sourceReferenceId}-${row.recordedAt}`}
                className="grid gap-0.5 border-t border-hairline py-3 [overflow-wrap:anywhere]"
              >
                <span className="text-[15px] leading-[22px]">
                  <strong>{evidenceEventLabels[row.event]}</strong>: {evidenceSummary(row)}
                </span>
                <Hint>
                  Recorded {formatDateTime(row.recordedAt)}. Policy {row.policyVersion}.
                </Hint>
                {conversation === null ? null : (
                  <Link className={cn(linkClasses, "text-sm")} to={conversation}>
                    Open message in the conversation
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </MemorySection>
  );
}

function RunSection({ detail }: Readonly<{ detail: StaffMemoryFactDetail }>) {
  const run = detail.run;
  return (
    <MemorySection id="fact-run" title="Extraction run">
      {run === null ? (
        <EmptyNote>
          {detail.fact.createdByKind === "system"
            ? "No extraction run is recorded for this memory."
            : "A person wrote this memory. No extraction run made it."}
        </EmptyNote>
      ) : (
        <>
          <MetaList wide>
            <MetaItem term="Run status">{runStatusLabels[run.status]}</MetaItem>
            <MetaItem term="Started because">{runTriggerLabels[run.trigger]}</MetaItem>
            <MetaItem term="Policy version">{run.policyVersion}</MetaItem>
            <MetaItem term="Prompt version">{run.extractorPromptVersion}</MetaItem>
            <MetaItem term="Model">{run.modelKey}</MetaItem>
          </MetaList>
          <Hint>The reasoning of the model is not stored and is never shown.</Hint>
        </>
      )}
    </MemorySection>
  );
}

function ChainStep({
  entry,
  index,
  current,
  customerId,
}: Readonly<{
  entry: StaffMemoryFactDetail["chain"][number];
  index: number;
  current: boolean;
  customerId: string;
}>) {
  const ownId = useId();
  const stepId = useId();
  return (
    <li
      className={cn(lineClasses, "gap-3", current ? "rounded-sm bg-accent px-3 py-2" : undefined)}
      aria-current={current ? "step" : undefined}
    >
      <span id={stepId} className="w-12 shrink-0 text-xs font-bold text-muted-foreground">
        Step {index + 1}
      </span>{" "}
      {current ? (
        <strong className="w-[110px] shrink-0 text-[15px] leading-[22px]">This memory</strong>
      ) : (
        <Link
          id={ownId}
          className={linkClasses}
          to={detailPath(customerId, entry.id)}
          aria-labelledby={nameWith(ownId, stepId)}
        >
          Open this version
        </Link>
      )}{" "}
      <FactStatusBadge status={entry.status} /> <ProvenanceBadge provenance={entry.provenance} />{" "}
      <span className="text-sm text-muted-foreground">
        Version {entry.version}. Created {formatDateTime(entry.createdAt)}.
      </span>
    </li>
  );
}

function ChainSection({
  detail,
  customerId,
}: Readonly<{ detail: StaffMemoryFactDetail; customerId: string }>) {
  return (
    <MemorySection id="fact-chain" title="Supersession chain">
      <Hint>Oldest first. A correction adds a new memory and replaces the old one.</Hint>
      <ol className="m-0 grid list-none gap-2 p-0">
        {detail.chain.map((entry, index) => (
          <ChainStep
            key={entry.id}
            entry={entry}
            index={index}
            current={entry.id === detail.fact.id}
            customerId={customerId}
          />
        ))}
      </ol>
    </MemorySection>
  );
}

type AuditRow = StaffMemoryFactDetail["audit"][number] & Readonly<{ rowId: string }>;
const auditColumn = createColumnHelper<AuditRow>();
const auditColumns = [
  auditColumn.accessor("occurredAt", {
    header: "When",
    meta: {
      headClassName: "first:pl-0 @max-[720px]/dt:first:pl-0",
      className: "first:pl-0 @max-[720px]/dt:first:pl-0",
    },
    cell: (info) => formatDateTime(info.getValue()),
  }),
  auditColumn.accessor("action", {
    header: "What happened",
    cell: (info) => auditActionLabel(info.getValue()),
  }),
  auditColumn.accessor("actorKind", {
    header: "Who",
    cell: (info) => actorKindLabels[info.getValue()],
  }),
  auditColumn.accessor("reasonCode", {
    header: "Reason",
    meta: {
      headClassName: "last:pr-0 @max-[720px]/dt:last:pr-0",
      className: "last:pr-0 @max-[720px]/dt:last:pr-0",
    },
    cell: (info) => {
      const code = info.getValue();
      return code === null ? "None recorded" : reasonCodeLabels[code];
    },
  }),
];

function AuditSection({ detail }: Readonly<{ detail: StaffMemoryFactDetail }>) {
  // Two audit rows can look the same (a retry that ran twice), so the position is part of the row id.
  const rows: readonly AuditRow[] = detail.audit.map((row, index) => ({
    ...row,
    rowId: `${index}-${row.action}-${row.occurredAt}-${row.actorKind}`,
  }));
  return (
    <MemorySection id="fact-audit" title={`Audit trail (${detail.audit.length})`}>
      {rows.length === 0 ? (
        <EmptyNote>No audit rows for this memory.</EmptyNote>
      ) : (
        <DataTable
          columns={auditColumns}
          data={rows}
          getRowId={(row) => row.rowId}
          caption="Audit trail, newest first"
          stickyHeader={false}
          className="rounded-none border-0"
          rowClassName={() => "h-11 border-hairline"}
        />
      )}
    </MemorySection>
  );
}
